import React from 'react';
import classes from './BioMarkerDetails.module.css';
import {
  IDiagnoseReferenceRanges,
  IDiagnoseResult,
  IDigitalHealthProfile,
} from '../UI/SelectedSalesOrder/DigitalHealthProfileTab';
import thumbsUpIcon from '../../assets/icons/thumbsup.svg';
import BackButton from '../UI/Buttons/BackButton';
import BiomarkerRanges from '../BioMarkerRange/BioMarkerRange';
import { DoctorLetterTypeId } from '../../enums/enums';
import { toast } from 'react-toastify';
import downloadIcon from '../../assets/icons/download-icon.svg';
import { useTranslation } from '../../hooks/useTranslations';

type Props = {
  diagnoseResult: IDiagnoseResult;
  onSelectResult: (diagnoseResult?: IDiagnoseResult, product?: IDigitalHealthProfile) => void;
  diagnose_reference_ranges: IDiagnoseReferenceRanges[];
  product: IDigitalHealthProfile;
};

const BioMarkerDetails: React.FC<Props> = ({ diagnoseResult, onSelectResult, product }) => {
  const { translations } = useTranslation();

  const handleDownload = () => {
    const doctorAttachmentFound: any = product?.attachments?.find((att: any) =>
      DoctorLetterTypeId.includes(att.attachment_type_id)
    );

    if (doctorAttachmentFound && doctorAttachmentFound.link) {
      window.open(
        `${window.location.protocol}//${window.location.host}${doctorAttachmentFound?.link}`
      );
    } else {
      toast.error(translations('error.no_attachments').toString(), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  };

  const range = React.useMemo(() => {
    return product.diagnose_reference_ranges?.filter(
      (range) => range.property_id === diagnoseResult.property_id
    );
  }, [diagnoseResult]);

  const normalRange = React.useMemo(() => {
    return range?.find((range) => range.reference_range_name === 'Normal');
  }, [range]);

  return (
    <div className={classes.container}>
      <div className={classes.upperSection}>
        <div className={classes.tilesContainer}>
          {product.diagnose_result?.map((tile, index) => (
            <div
              key={index}
              style={{
                color: `#${tile.color_code}`,
              }}
              className={`${classes.tile}
               ${tile.property_id === diagnoseResult.property_id ? classes.selectedTile : ''}`}
              onClick={() => onSelectResult(tile, product)}
            >
              <div
                className={classes.tileBackground}
                style={{ backgroundColor: `#${tile.color_code}`, opacity: 0.3 }}
              />
              {tile.property_short_abbreviation}
            </div>
          ))}
        </div>
        <div className={classes.btnWrapp}>
          <div
            className={classes.downloadBtn}
            onClick={handleDownload}
            style={{ cursor: 'pointer' }}
          >
            <span>{translations('download')}</span>
            <img src={downloadIcon} alt="download" />
          </div>
        </div>
        <div className={classes.header}>
          <div className={classes.title}>
            <BackButton onClick={() => onSelectResult()} disableMargin iconOnly />
            {`${diagnoseResult.property_name} (${diagnoseResult.property_short_abbreviation})`}
          </div>
          <div className={classes.legendContainer}>
            <div className={classes.legendItem}>
              <div className={classes.redDot} />
              {translations('biomarkerDetails.veryLowHigh')}
            </div>
            <div className={classes.legendItem}>
              <div className={classes.greenDot} />
              {translations('biomarkerDetails.normal')}
            </div>
            <div className={classes.legendItem}>
              <div className={classes.orangeDot} />
              {translations('biomarkerDetails.lowHigh')}
            </div>
          </div>
        </div>
        <div style={{ height: 160 }}>
          <BiomarkerRanges product={product} biomarker={diagnoseResult} />
          <div
            style={{
              textAlign: 'center',
              marginTop: 10,
              marginBottom: 100,
              fontSize: 18,
              fontWeight: 600,
            }}
          >
            {translations('biomarkerDetails.normal.range')}:{' '}
            {normalRange?.property_range?.replace(',', ' - ')?.replace('[', '')?.replace(']', '')}
          </div>
        </div>
      </div>
      <div className={classes.sectionContainer}>
        <div className={classes.sectionHeader}>
          <img src={thumbsUpIcon} alt={'thumbs-up'} className={classes.sectionIcon} />
          <div className={classes.sectionTitle}>{translations('biomarkerDetails.insights')}</div>
        </div>
        <div className={classes.textContainer}>
          <div className={classes.question}>{translations('biomarkerDetails.what')}</div>
          <div className={classes.answer}>{diagnoseResult.property_what}</div>
        </div>
        <div className={classes.textContainer}>
          <div className={classes.question}>{translations('biomarkerDetails.how')}</div>
          <div className={classes.answer}>
            {diagnoseResult.reference_summary ?? diagnoseResult.reference_introductory_statement}
          </div>
        </div>
      </div>
    </div>
  );
};

export { BioMarkerDetails };
